import React, { useMemo } from 'react';
import { DownloadIcon } from './icons';

interface InstallModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInstallPwa: () => void;
  apkUrl?: string;
  isInstallPromptAvailable: boolean;
}

const Icons = {
  android: (
    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.523 15.3414c-.5511 0-.9978-.4467-.9978-.9978 0-.5511.4467-.9978.9978-.9978.5511 0 .9978.4467.9978.9978 0-.5511-.4467.9978-.9978.9978zm-11.046 0c-.5511 0-.9978-.4467-.9978-.9978 0-.5511.4467-.9978.9978-.9978.5511 0 .9978.4467.9978.9978 0-.5511-.4467.9978-.9978.9978zm11.4045-6.02l1.9973-3.4592c.1111-.1921.0451-.4376-.147-.5487-.192-.1111-.4375-.0451-.5486.147L17.1517 8.889c-1.5457-.7049-3.2644-1.1017-5.0717-1.1017-1.8073 0-3.526.3968-5.0717 1.1017l-1.9803-3.4293c-.1111-.192-.3566-.258-.5487-.147-.1921.1111-.2581.3566-.147.5487l1.9973 3.4592C3.1206 10.9575 1 13.7225 1 16.9242h22.16c0-3.2017-2.1206-5.9667-5.324-7.6028z" />
    </svg>
  ),
  web: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9-9c1.657 0 3 4.03 3 9s-1.343 9-3 9m0-18c-1.657 0-3 4.03-3 9s1.343 9 3 9m-9-9a9 9 0 019-9" />
    </svg>
  ),
  apple: (
    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.1 2.48-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .76-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91 1.65.07 2.89.67 3.55 1.64-1.34.81-2.25 2.15-2.22 3.75.03 2.01 1.63 2.98 3.52 3.88-.34 1.34-1.07 2.62-1.89 4.39zM13 3.5c.73-.89 1.22-2.11 1.09-3.32-1.05.04-2.31.7-3.07 1.58-.68.79-1.28 2.02-1.12 3.22 1.17.09 2.33-.59 3.1-1.48z" />
    </svg>
  ),
  mobile: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  ),
  chevronRight: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  )
};

const InstallModal: React.FC<InstallModalProps> = ({ 
  isOpen, 
  onClose, 
  onInstallPwa, 
  apkUrl = "https://mesurette.com/app/carnet-mesures.apk",
  isInstallPromptAvailable
}) => {
  const isiOS = useMemo(() => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
      />
      
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden animate-scaleUp">
        {/* Header */}
        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-6 text-white">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <h2 className="text-2xl font-bold mb-2">Installez l'Application</h2>
          <p className="text-blue-100 text-sm">Choisissez la méthode d'installation adaptée à votre appareil.</p>
        </div>

        <div className="p-6 space-y-4">
          {/* Option 1: PWA (Recommended) */}
          <button
            onClick={() => {
              onInstallPwa();
              onClose();
            }}
            className="w-full group relative flex items-center gap-4 p-5 rounded-2xl border-2 border-blue-100 bg-blue-50/30 hover:border-blue-500 hover:bg-blue-50 transition-all duration-300 text-left"
          >
            <div className="absolute top-0 right-4 -translate-y-1/2">
              <span className="px-3 py-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[10px] font-bold rounded-full shadow-lg">
                RECOMMANDÉ
              </span>
            </div>
            
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-lg shadow-blue-500/20 shrink-0 group-hover:scale-110 transition-transform">
              {isiOS ? Icons.apple : Icons.web}
            </div>
            
            <div className="flex-1">
              <h3 className="font-bold text-gray-800 text-lg group-hover:text-blue-700 transition-colors">
                {isiOS ? 'Ajouter à l\'écran d\'accueil' : 'Installation Rapide'}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {isiOS 
                  ? 'Utilisez le bouton Partager de Safari pour installer l\'app sur votre iPhone.' 
                  : 'S\'installe en 1 clic via votre navigateur. Accès instantané et hors-ligne.'}
              </p>
            </div>
            
            <div className="text-blue-400 group-hover:translate-x-1 transition-transform">
              {Icons.chevronRight}
            </div>
          </button>

          {/* Option 2: APK (Android only) */}
          {!isiOS && (
            <a
              href={apkUrl}
              className="w-full group flex items-center gap-4 p-5 rounded-2xl border-2 border-gray-100 bg-gray-50/30 hover:border-emerald-500 hover:bg-emerald-50 transition-all duration-300 text-left"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-lg shadow-emerald-500/20 shrink-0 group-hover:scale-110 transition-transform">
                {Icons.android}
              </div>
              
              <div className="flex-1">
                <h3 className="font-bold text-gray-800 text-lg group-hover:text-emerald-700 transition-colors">
                  Version Android Native
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Téléchargez le fichier APK pour une installation classique sur votre téléphone Android.
                </p>
              </div>
              
              <div className="text-emerald-400 group-hover:translate-x-1 transition-transform">
                {Icons.chevronRight}
              </div>
            </a>
          )}
          
          {/* Footer Info */}
          <div className="pt-2 px-2">
            <p className="text-[11px] text-gray-400 text-center leading-relaxed italic">
              Les deux versions offrent les mêmes fonctionnalités : gestion des clients, mesures, commandes et synchronisation cloud.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleUp {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-scaleUp {
          animation: scaleUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }
      `}</style>
    </div>
  );
};

export default InstallModal;
